<template>
  <div class="start-up-report">
    <table v-if="chartingData" class="report-table">
      <tr>
        <th></th>
        <th>Design Point</th>
        <th
          class="text-center bg-gray-400"
          :colspan="hypotheticalFrequenciesHz?.length"
        >
          Pump Variable Frequency Analysis
        </th>
      </tr>
      <StartUpReportRow
        title="Frequency"
        field="pumpHz"
        :baseUnit="Hertz"
        :targetUnit="Hertz"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Liquid Flow"
        field="liquidFlowRateSTBD"
        :baseUnit="LiquidFlowRateUnits.BarrelsPerDay"
        :targetUnit="targetLiquidFlowUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Intake Pressure"
        field="pumpIntakePressurePSIA"
        :baseUnit="PressureUnits.PoundsPerSquareInchAbsolute"
        :targetUnit="targetPressureUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Discharge Pressure"
        field="dischargePressurePSIA"
        :baseUnit="PressureUnits.PoundsPerSquareInchAbsolute"
        :targetUnit="targetPressureUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Q Oil"
        field="oilFlowRateSTBD"
        :baseUnit="LiquidFlowRateUnits.BarrelsPerDay"
        :targetUnit="targetLiquidFlowUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Q Water"
        field="waterFlowRateSTBD"
        :baseUnit="LiquidFlowRateUnits.BarrelsPerDay"
        :targetUnit="targetLiquidFlowUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="Q Gas"
        field="gasFlowRateSCFD"
        :baseUnit="GasFlowRateUnits.CubicFeetPerDay"
        :targetUnit="targetGasFlowUnit"
        :round="true"
        :data="allReportCalcs"
      />
      <StartUpReportRow
        title="TDH"
        field="producedTotalDynamicHeadFt"
        :baseUnit="LengthUnits.Feet"
        :targetUnit="targetLargeLengthUnit"
        :round="true"
        :data="allReportCalcs"
      />
    </table>
  </div>
  <p class="body-3">
    * N/A values are a result of insufficient discharge pressure or pumped-off
    well conditions
  </p>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { LengthUnits } from "@/libs/units/length";
import { GasFlowRateUnits, LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { PressureUnits } from "@/libs/units/pressure";
import StartUpReportRow from "./StartUpReportRow.vue";
import { Hertz } from "@/libs/units/frequency";
import type {
  PumpTornadoChartViewData,
  StartupTdhReportCalcRow
} from "@/libs/charting";
import { usePumpChartingData } from "@/libs/charting";
import type { Scenario, Sizing } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { shouldShowOilRates } from "@/utils/scenarioUtils";
import { useDisplayUnitsQuery } from "gql/api";
import {
  scenarioKey,
  sizingKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  minFrequency: number;
  maxFrequency: number;
  scenario?: Scenario;
  sizing?: Sizing;
  scenarioCalculations?: ScenarioCalculations;
  pumpTornadoChartData?: PumpTornadoChartViewData;
}>();

const _scenario = props.scenario ?? injectStrict(scenarioKey);
const _sizing = props.sizing ?? injectStrict(sizingKey, null);
const _scenarioCalculations =
  props.scenarioCalculations ?? injectStrict(scenarioCalculationsKey);

const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getGasFlowUnit,
  getPressureUnit
} = useScenarioUnits(_scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetGasFlowUnit = getGasFlowUnit("Gas Rate.write.mscf/d");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);

const chartingData = computed(() => {
  if (!props.pumpTornadoChartData) {
    return usePumpChartingData(
      props.minFrequency,
      props.maxFrequency,
      _scenarioCalculations,
      targetLiquidFlowUnit.value,
      targetPowerUnit.value,
      targetLargeLengthUnit.value,
      targetPressureUnit.value
    );
  } else {
    return props.pumpTornadoChartData;
  }
});

const displayUnits = useDisplayUnitsQuery();
const currentScenarioShowsOilRates = computed(() =>
  shouldShowOilRates(displayUnits.data.value, props.scenario)
);

const hypotheticalFrequenciesHz = computed(() => {
  return chartingData.value?.context.hypotheticalPumpFrequenciesHz;
});

// Whenever US Water Well/Europe Geothermal/Turkey Geothermal is selected
// as the unit type for a scenario, the reports pages should exclude any oil references
const allReportCalcs = computed<StartupTdhReportCalcRow[]>(
  () =>
    chartingData.value?.tdhStartupRows?.map(r => {
      return {
        ...r,
        oilFlowRateSTBD: currentScenarioShowsOilRates.value
          ? r.oilFlowRateSTBD
          : NaN
      };
    }) || []
);
</script>
